import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Copy, Loader2, Sparkles, Plus, ClipboardList, Mail, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { AdminHeading, Panel, adminInput } from "@/components/admin/ui";
import { GhostIcon } from "@/components/ghost/GhostIcon";

const DRAFT_TYPES = [
  ["guest_email", "Guest email"], ["reservation_message", "Reservation message"],
  ["concierge_reply", "Concierge reply"], ["payment_reminder", "Payment reminder"],
  ["welcome_message", "Welcome message"], ["thank_you", "Thank-you message"],
  ["feedback_follow_up", "Feedback follow-up"], ["lost_item_update", "Lost-item update"],
  ["referral_response", "Referral response"], ["event_message", "Event message"],
];

const BRIEFINGS = [
  ["today", "Today's Briefing"], ["arrivals", "Arrivals Briefing"], ["in_house", "In-House Briefing"],
  ["event", "Event Briefing"], ["operations", "Operations Plan"], ["finance", "Finance Summary"],
  ["comms", "Communications Summary"], ["maintenance", "Maintenance Summary"], ["post_stay", "Post-Stay Summary"],
];

export default function AdminD() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [bLoading, setBLoading] = useState("");
  const [draft, setDraft] = useState({ draft_type: "guest_email", reservation_id: "", to: "", notes: "" });
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/ghost/config").then((r) => setCfg(r.data)).catch(() => setCfg(false)); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending]);

  const runDraft = async () => {
    setDrafting(true); setDraftResult(null);
    try {
      const { data } = await api.post("/ghost/admin/draft-communication", draft);
      if (data.ok) { setDraftResult(data); toast.success("D drafted a message — review & approve in Communications."); }
      else toast.message(data.reply || "D is unavailable.");
    } catch (e) { toast.error(apiErr(e)); } finally { setDrafting(false); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next); setInput(""); setSending(true);
    try {
      const { data } = await api.post("/ghost/admin/chat", { messages: next, conversation_id: convId, context_type: "command_center" });
      setConvId(data.conversation_id);
      setMessages([...next, { role: "assistant", content: data.reply, fallback: data.fallback, question: msg }]);
    } catch (e) { setMessages([...next, { role: "assistant", content: apiErr(e), fallback: true, question: msg }]); }
    finally { setSending(false); }
  };

  const runBriefing = async (kind, label) => {
    setBLoading(kind); setBriefing(null);
    try { const { data } = await api.get(`/ghost/admin/briefing/${kind}`); setBriefing({ label, ...data }); }
    catch (e) { toast.error(apiErr(e)); } finally { setBLoading(""); }
  };

  const saveAsTask = async (question) => {
    try { await api.post("/admin/tasks", { title: `From D: ${question.slice(0, 80)}`, category: "general", priority: "normal", notes: question }); toast.success("Saved as a task."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (cfg === false) return <p className="text-muted-foreground">Unable to load D.</p>;
  const name = cfg?.display_name || "D";

  return (
    <div>
      <AdminHeading title="D Command Center" subtitle="Your private AI operations assistant — briefings, summaries, and drafts. You confirm every action." />
      {cfg && cfg.status !== "configured" && (
        <div className="mb-5 px-4 py-3 rounded-lg text-sm text-amber-300/90 bg-amber-400/5 border border-amber-400/20" data-testid="ghost-status-banner">
          {name} is currently {cfg.status === "not_configured" ? "not configured" : cfg.status}. Briefings and chat will return a neutral message until the AI assistant is available.
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Panel title="Briefings">
            <div className="space-y-2">
              {BRIEFINGS.map(([k, label]) => (
                <button key={k} onClick={() => runBriefing(k, label)} data-testid={`briefing-${k}`}
                  className="w-full text-left px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-pool hover:bg-white/5 flex items-center justify-between">
                  {label} {bLoading === k && <Loader2 size={14} className="animate-spin" />}
                </button>
              ))}
            </div>
          </Panel>
          <Panel title="Quick Prompts">
            <div className="flex flex-wrap gap-2">
              {(cfg?.admin_quick_prompts || []).map((p, i) => (
                <button key={i} onClick={() => send(p)} data-testid={`ghost-cc-quick-${i}`}
                  className="text-xs text-pool border border-pool/30 rounded-full px-3 py-1.5 hover:bg-pool/10 text-left">{p}</button>
              ))}
            </div>
          </Panel>
          <Panel title={<span className="flex items-center gap-2"><Mail size={14} /> Draft a Guest Message</span>}>
            <p className="text-xs text-muted-foreground mb-3">D writes a draft. You review, edit & approve it in Communications — nothing sends automatically.</p>
            <div className="space-y-2">
              <select className={adminInput} value={draft.draft_type} onChange={(e) => setDraft({ ...draft, draft_type: e.target.value })} data-testid="draft-type">
                {DRAFT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input className={adminInput} placeholder="Reservation ID (optional)" value={draft.reservation_id} onChange={(e) => setDraft({ ...draft, reservation_id: e.target.value })} data-testid="draft-rid" />
              <input className={adminInput} placeholder="Recipient email (optional)" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} data-testid="draft-to" />
              <textarea className={adminInput} rows={2} placeholder="Notes for D (optional)" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
              <button onClick={runDraft} disabled={drafting} className="btn-gold w-full py-2 text-sm flex items-center justify-center gap-2" data-testid="ghost-draft-btn">
                {drafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Draft with D
              </button>
              {draftResult && (
                <div className="mt-2 border border-pool/20 rounded-lg p-3 bg-black/20" data-testid="draft-result">
                  <p className="text-xs text-muted-foreground mb-1">Draft (to {draftResult.to || "—"}):</p>
                  <p className="text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">{draftResult.body}</p>
                  <button onClick={() => nav("/admin/communications")} className="text-xs text-pool mt-2 inline-flex items-center gap-1" data-testid="draft-review-link"><ExternalLink size={12} /> Review in Communications</button>
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {briefing && (
            <Panel title={briefing.label} action={<button onClick={() => navigator.clipboard?.writeText(briefing.summary)} className="text-xs text-muted-foreground hover:text-gold flex items-center gap-1"><Copy size={12} /> Copy</button>}>
              <p className="text-sm whitespace-pre-wrap text-foreground/90" data-testid="briefing-result">{briefing.summary}</p>
            </Panel>
          )}

          <div className="bg-surface/60 border border-white/10 rounded-lg flex flex-col h-[520px]">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
              <GhostIcon size={34} />
              <div><p className="font-serif text-lg text-gold">Ask {name}</p><p className="text-[10px] tracking-label uppercase text-muted-foreground">{cfg?.subtitle}</p></div>
              <button onClick={() => { setMessages([]); setConvId(null); }} aria-label="New conversation" className="ml-auto p-2 text-muted-foreground hover:text-gold"><Plus size={16} /></button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="ghost-cc-messages">
              {messages.length === 0 && <p className="text-sm text-muted-foreground">{cfg?.admin_greeting}</p>}
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  {m.role === "assistant" && <GhostIcon size={28} glow={false} className="shrink-0" />}
                  <div className={`max-w-[85%] ${m.role === "user" ? "text-right" : ""}`}>
                    <div className={`text-sm whitespace-pre-wrap px-4 py-3 rounded-xl ${m.role === "user" ? "bg-pool/10 rounded-tr-none" : "glass rounded-tl-none"}`}>{m.content}</div>
                    {m.role === "assistant" && !m.fallback && <button onClick={() => navigator.clipboard?.writeText(m.content)} className="text-[10px] text-muted-foreground hover:text-gold mt-1 inline-flex items-center gap-1"><Copy size={11} /> Copy</button>}
                    {m.role === "assistant" && m.fallback && <button onClick={() => saveAsTask(m.question)} className="text-[11px] text-pool hover:underline mt-1 inline-flex items-center gap-1"><ClipboardList size={12} /> Save as task</button>}
                  </div>
                </div>
              ))}
              {sending && <div className="flex gap-3"><GhostIcon size={28} glow={false} /><div className="glass rounded-xl rounded-tl-none px-4 py-3 flex items-center gap-2 text-muted-foreground text-sm"><Loader2 size={14} className="animate-spin" /> {name} is thinking…</div></div>}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex items-end gap-2">
              <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={`Ask ${name} anything about The D…`} data-testid="ghost-cc-input"
                className="flex-1 resize-none bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-pool focus:outline-none max-h-28" />
              <button type="submit" disabled={sending || !input.trim()} className="btn-gold p-2.5 rounded-xl disabled:opacity-40" data-testid="ghost-cc-send"><Send size={17} /></button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
